// Stage Mail-V2: useMailMessages — paginated list of messages.

import { useEffect, useState } from "react";
import { mailV2Api } from "../../../api/mail-v2";
import { mapMessage, mapFolderIdToMailboxName, type MessageV2View } from "../api/mappers";

type Args = {
  accountId: string | null;
  folderId: string;
  search?: string;
  page: number;
  pageSize?: number;
  /** Stage Mail-V2 Step 17: 限定到特定 label 的 message 列表 (label-only mailbox) */
  labelId?: string;
};

type State = {
  items: MessageV2View[];
  total: number;
  page: number;
  pageSize: number;
  loading: boolean;
  error: string | null;
  refresh: () => void;
};

export function useMailMessages(args: Args): State {
  const [items, setItems] = useState<MessageV2View[]>([]);
  const [total, setTotal] = useState<number>(0);
  const [loading, setLoading] = useState<boolean>(true);
  const [error, setError] = useState<string | null>(null);
  const [reload, setReload] = useState<number>(0);

  useEffect(() => {
    let cancelled = false;
    // ★ 2026-07-23 真凶修复: hooks 顺序违规的根因。
    // 旧版 _useMailMessagesReal wrapper 在 enabled=false / folderId='drafts' 时
    // 短路返 _emptyHookShim (0 hooks),其他时候返 useMailMessages (5 useState + 1 useEffect = 6 hooks)。
    // 两次 render 之间一旦切换,后续所有 hooks 全部错位 →
    // React 报 "useState vs useRef" 顺序不符 + TypeError "Cannot read properties of undefined (reading 'length')"。
    // 修复: 短路逻辑挪到 hook 内部 (无条件 return),hook 调用次数保持不变。
    if (!args.accountId) {
      // enabled=false 模拟 — 不打 API,返空。
      setItems([]);
      setTotal(0);
      setLoading(false);
      return;
    }
    if (args.folderId === "drafts") {
      // Drafts folder 走 mail_drafts 表(mail_messages.mailbox_name='DRAFTS' 永远 NULL),
      // _useMailMessagesReal 已在 MailV2Page 用 draftsHook.items 适配 — 这里保持返空避免打错 endpoint。
      setItems([]);
      setTotal(0);
      setLoading(false);
      return;
    }
    setLoading(true);
    setError(null);
    (async () => {
      try {
        const mailbox = mapFolderIdToMailboxName(args.folderId);
        const res = await mailV2Api.listMessages({
          mail_account_id: args.accountId ?? undefined,
          mailbox: args.labelId ? undefined : mailbox,
          search: args.search || undefined,
          page: args.page,
          page_size: args.pageSize ?? 25,
          label_id: args.labelId || undefined,
        } as unknown as Parameters<typeof mailV2Api.listMessages>[0]);
        if (cancelled) return;
        setItems((res.items ?? []).map(mapMessage));
        setTotal(res.total ?? 0);
      } catch (e) {
        if (!cancelled) {
          // eslint-disable-next-line no-console
          console.error("[useMailMessages] FAIL", {
            folderId: args.folderId,
            mailbox: mapFolderIdToMailboxName(args.folderId),
            accountId: args.accountId,
            error: e instanceof Error ? e.message : String(e),
          });
          setError(e instanceof Error ? e.message : String(e));
          setItems([]);
          setTotal(0);
        }
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [
    args.accountId,
    args.folderId,
    args.search,
    args.page,
    args.pageSize,
    args.labelId,
    reload,
  ]);

  return {
    items,
    total,
    page: args.page,
    pageSize: args.pageSize ?? 25,
    loading,
    error,
    refresh: () => setReload((n) => n + 1),
  };
}
